import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import type { FullTrip } from 'tripPlanner/types/common'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import FormatDateTimeRange from 'components/utils/Formatters/FormatDateTimeRange'
import BodyTextBlock from '../../../LuxKit/TextBlocks/BodyTextBlock'
import { FormattedMessage, useIntl } from 'react-intl'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'

interface Props {
  trip: FullTrip
}
function TripLandingPageHeader(props: Props) {
  const intl = useIntl()

  const { trip } = props
  const daysUntilNextTrip = trip.startDate ? daysUntilDate(trip.startDate.toDate()) : null

  const firstDestination = trip.destinationsGeo?.[0]
  const firstDestinationName = firstDestination
    ? intl.formatMessage(
        {
          id: 'FWw1cseIFarbS5i_vfN8',
          defaultMessage: '{destinationName}, {countryCode}',
          description: 'Trip landing page header destination name and country code separated by comma',
        },
        { destinationName: firstDestination.shortName, countryCode: firstDestination.countryCode },
      )
    : ''

  const numberOfAdults = trip.travellerRooms?.[0]?.adults
  const numberOfChildren = trip.travellerRooms?.[0]?.childAges?.length
  const hasChildren = !!(numberOfChildren && numberOfChildren > 0)

  const renderTravellers = () => {
    if (hasChildren) {
      return (
        <FormattedMessage
          id="fPkPMekCaq2YJYL6mWS3"
          defaultMessage="{adultCountLabel}, {childCountLabel}"
          description="Traveller count with adults and children in the traveller form"
          values={{
            adultCountLabel: intl.formatMessage({
              id: 'DI1E5wbXrR3pMfELKlKV',
              defaultMessage: '{adults, plural, one {# adult} other {# adults}}',
              description: 'Adult count in trip landing header traveller summary',
            }, { adults: numberOfAdults }),
            childCountLabel: intl.formatMessage({
              id: '7rWkFlJE9Hq3BrKbeIVM',
              defaultMessage: '{children, plural, one {# child} other {# children}}',
              description: 'Child count in trip landing header traveller summary',
            }, { children: numberOfChildren }),
          }}
        />
      )
    }
    return (
      <FormattedMessage
        id="grLWHXp5LO8dplSe1MH-"
        defaultMessage="{adults, plural, one {# adult} other {# adults}}"
        description="Traveller count with adults only in the traveller details form"
        values={{
          adults: numberOfAdults,
        }}
      />
    )
  }

  return (
    <Sheet
      paletteType="inverse"
      style={{ position: 'relative' }}
    >
      <Group direction="horizontal" gap={24} verticalAlign="center" horizontalAlign="space-between">
        <Group direction="vertical" gap={16}>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {!!daysUntilNextTrip && daysUntilNextTrip > 0 && (
              <>
                <StatusLabel
                  variant="confirmation"
                >
                  <FormattedMessage
                    id="p_z5J4U3feDmKmc1nsob"
                    defaultMessage="{count, plural, one {# Day} other {# Days}} to go"
                    description="Days until trip countdown label in the trip planner feature"
                    values={{ count: daysUntilNextTrip }}
                  />
                </StatusLabel>
                <BodyText variant="medium">
                  <FormattedMessage
                    id="JjL3eaFFSoDfPXZHmkcf"
                    defaultMessage="until your next trip"
                    description="Text indicating until next trip in the trip planner feature"
                  />
                </BodyText>
              </>
            )}
          </Group>
          <HeadingTextBlock variant="heading1">
            <Heading variant="heading1">
              {trip.name}
            </Heading>
          </HeadingTextBlock>
          <Group direction="horizontal" gap="4 16" wrap="wrap">
            {firstDestinationName && (
              <BodyTextBlock variant="large" startIcon={<LineMapMarkerIcon />}>
                {firstDestinationName}
              </BodyTextBlock>
            )}
            {trip.startDate && trip.endDate && (
              <BodyTextBlock variant="large" startIcon={<LineCalendarIcon />}>
                <FormatDateTimeRange startValue={trip.startDate} endValue={trip.endDate} format="short-day-name-day-month-year" />
              </BodyTextBlock>
            )}
            {!!numberOfAdults && (
              <BodyTextBlock variant="large" startIcon={<LineUsersAltIcon />}>
                {renderTravellers()}
              </BodyTextBlock>
            )}
          </Group>
          <Group direction="horizontal" gap={16} verticalAlign="center">
            <TextButton
              kind="primary"
              size="large"
              shape="round"
              to={`/trip-planner/trip/${trip.id}/itinerary`}
            >
              <FormattedMessage
                id="4FpKf0Au5JDMHVB-WiQC"
                defaultMessage="View trip plan"
                description="Button text to navigate to and view the full trip plan in the trip planner tool"
              />
            </TextButton>
            <TextButton
              kind="secondary"
              size="large"
              startIcon={<SolidSparklesIcon />}
              shape="round"
              to={CHAT_TRIP_PLANNER_NEW_PATH}
            >
              <FormattedMessage
                id="tVzNFj9VQGdbiEU-TIjC"
                defaultMessage="Plan new trip"
                description="Button text to plan a new trip in the trip planner feature"
              />
            </TextButton>
          </Group>
        </Group>
      </Group>
    </Sheet>
  )
}

export default TripLandingPageHeader
